<template>
  <v-app>
    <v-main class="invitation-page">
      <v-container fluid fill-height>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="10" md="8" lg="6" xl="5">
            
            <!-- Loading State -->
            <v-card v-if="loading" class="invitation-card elevation-24">
              <div class="text-center py-16">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="60"
                  width="6"
                ></v-progress-circular>
                <h5 class="text-h6 text-typo mt-6 mb-0 font-weight-medium">Lade Einladung...</h5>
              </div>
            </v-card>

          <!-- Einladung validieren und anzeigen -->
          <v-card v-if="!loading && !accepted && invitation" class="invitation-card elevation-24">
            <!-- Header -->
            <div class="invitation-header">
              <div class="icon-wrapper">
                <i class="material-icons-round">mail</i>
              </div>
              <h4 class="header-title">Manager-Einladung</h4>
              <p class="header-subtitle">oelsnitz-ticket.de</p>
            </div>

            <v-card-text class="pa-8">
              <!-- Intro -->
              <p class="text-center text-typo text-h6 font-weight-regular mb-6">
                Du wurdest eingeladen mit folgenden Berechtigungen zu arbeiten
              </p>

              <!-- Info Box -->
              <div class="info-box mb-6">
                <div class="info-row">
                  <i class="material-icons-round">business</i>
                  <div>
                    <div class="info-label">Organisation</div>
                    <div class="info-value">{{ invitation.forManager.companyName || invitation.forManager.email }}</div>
                  </div>
                </div>
                <v-divider class="my-3"></v-divider>
                <div class="info-row">
                  <i class="material-icons-round">email</i>
                  <div>
                    <div class="info-label">Deine Email</div>
                    <div class="info-value">{{ invitation.email }}</div>
                  </div>
                </div>
                <v-divider class="my-3"></v-divider>
                <div class="info-row">
                  <i class="material-icons-round">badge</i>
                  <div>
                    <div class="info-label">Deine Berechtigungen</div>
                    <div class="info-value d-flex flex-wrap ga-1">
                      <v-chip 
                        v-if="invitation.canManage"
                        small 
                        color="#cb0c9f" 
                        text-color="white"
                        class="font-weight-bold"
                      >
                        Manager
                      </v-chip>
                      <v-chip 
                        v-if="invitation.canPos"
                        small 
                        color="#4caf50" 
                        text-color="white"
                        class="font-weight-bold"
                      >
                        POS
                      </v-chip>
                      <v-chip 
                        v-if="invitation.canCheck"
                        small 
                        color="#00bcd4" 
                        text-color="white"
                        class="font-weight-bold"
                      >
                        Kontrolleur
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Permissions -->
              <div class="permissions-box mb-6">
                <h6 class="permissions-title">
                  <i class="material-icons-round">verified_user</i>
                  Deine Berechtigungen
                </h6>
                <ul class="permissions-list">
                  <li v-if="invitation.canManage">
                    <i class="material-icons-round">check</i>
                    Verwaltung von Events, News und Einstellungen
                  </li>
                  <li v-if="invitation.canManage">
                    <i class="material-icons-round">check</i>
                    Einladen anderer Manager
                  </li>
                  <li v-if="invitation.canManage">
                    <i class="material-icons-round">check</i>
                    Archivieren von Events
                  </li>
                  
                  <li v-if="invitation.canPos">
                    <i class="material-icons-round">check</i>
                    Erstellen von Tickets im Point-of-Sale
                  </li>
                  <li v-if="invitation.canPos">
                    <i class="material-icons-round">check</i>
                    Ansicht von Event-Details
                  </li>
                  
                  <li v-if="invitation.canCheck">
                    <i class="material-icons-round">check</i>
                    Scannen und Validieren von Tickets
                  </li>
                  <li v-if="invitation.canCheck">
                    <i class="material-icons-round">check</i>
                    Kontrolle der Ticket-Gültigkeit
                  </li>
                </ul>
              </div>

              <!-- Status Messages -->
              <div v-if="managerExists" class="status-box success mb-6">
                <i class="material-icons-round">check_circle</i>
                <div>
                  <strong>Account vorhanden</strong>
                  <p v-if="invitation.role === 'checker'">
                    {{ getExistingManagerName() }} - Nach Bestätigung kannst du dich anmelden
                  </p>
                  <p v-else>
                    {{ getExistingManagerName() }} - Du wirst zum Login weitergeleitet
                  </p>
                </div>
              </div>

              <div v-if="!managerExists" class="status-box info mb-6">
                <i class="material-icons-round">person_add</i>
                <div>
                  <strong>Neuer Account</strong>
                  <p v-if="invitation.role === 'checker'">
                    Ein Account wird für Dich erstellt - Anschließend kannst du dich anmelden
                  </p>
                  <p v-else>
                    Ein Account wird für Dich erstellt
                  </p>
                </div>
              </div>

              <div v-if="invitation.expiresAt" class="status-box" :class="isExpiringSoon ? 'warning' : 'default'" style="margin-bottom: 0;">
                <i class="material-icons-round">schedule</i>
                <div>
                  <strong>Läuft ab am {{ formatDate(invitation.expiresAt) }}</strong>
                  <p v-if="isExpiringSoon">Bitte akzeptiere sie bald!</p>
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="pa-8 pt-0 d-flex justify-center">
              <v-btn
                x-large
                :loading="accepting"
                :disabled="accepting"
                @click="acceptInvitation"
                class="accept-btn"
                elevation="0"
              >
                <i class="material-icons-round me-2">check_circle</i>
                Einladung annehmen
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Fehler / Einladung nicht gefunden -->
          <v-card v-if="!loading && !accepted && error" class="invitation-card elevation-24 error-card">
            <div class="error-header">
              <div class="error-icon-wrapper">
                <i class="material-icons-round">error_outline</i>
              </div>
              <h4 class="error-title">Einladung ungültig</h4>
            </div>

            <v-card-text class="pa-8">
              <p class="text-center text-typo text-body-1 mb-6">
                {{ error }}
              </p>

              <div class="error-reasons mb-6">
                <h6 class="text-typo font-weight-bold mb-3">Mögliche Gründe:</h6>
                <ul>
                  <li>Die Einladung ist abgelaufen</li>
                  <li>Die Einladung wurde bereits angenommen</li>
                  <li>Der Link ist ungültig</li>
                </ul>
              </div>

              <p class="text-center text-muted text-body-2">
                Kontaktiere den Administrator, wenn Du glaubst, dass dies ein Fehler ist.
              </p>
            </v-card-text>

            <v-card-actions class="pa-8 pt-0 d-flex justify-center">
              <v-btn
                x-large
                outlined
                color="primary"
                @click="$router.push('/')"
                elevation="0"
                class="px-8"
              >
                <i class="material-icons-round me-2">home</i>
                Zur Website
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Erfolgreich akzeptiert -->
          <v-card v-if="!loading && accepted" class="invitation-card elevation-24 success-card">
            <div class="success-header">
              <div class="success-icon-wrapper">
                <i class="material-icons-round">check_circle</i>
              </div>
              <h4 class="success-title">Einladung angenommen!</h4>
              <p class="success-subtitle">Willkommen im Team</p>
            </div>

            <v-card-text class="pa-8 text-center">
              <p class="text-h6 text-typo font-weight-regular mb-4">
                Du wurdest erfolgreich mit folgenden Berechtigungen hinzugefügt:
              </p>
              
              <div class="d-flex justify-center flex-wrap ga-2 mb-4">
                <v-chip 
                  v-if="acceptedPermissions.canManage"
                  color="#cb0c9f" 
                  dark
                  class="font-weight-bold"
                >
                  Manager
                </v-chip>
                <v-chip 
                  v-if="acceptedPermissions.canPos"
                  color="#4caf50" 
                  dark
                  class="font-weight-bold"
                >
                  POS
                </v-chip>
                <v-chip 
                  v-if="acceptedPermissions.canCheck"
                  color="#00bcd4" 
                  dark
                  class="font-weight-bold"
                >
                  Kontrolleur
                </v-chip>
              </div>

              <div v-if="!acceptedPermissions.canCheck || acceptedPermissions.canManage || acceptedPermissions.canPos" class="redirect-info">
                <v-progress-circular
                  indeterminate
                  color="success"
                  size="40"
                  width="4"
                  class="mb-3"
                ></v-progress-circular>
                <p class="text-body-1 text-muted mb-0">
                  {{ isUserLoggedIn ? 'Weiterleitung zum Admin-Bereich...' : 'Weiterleitung zum Login...' }}
                </p>
              </div>

              <div v-if="acceptedPermissions.canCheck && !acceptedPermissions.canManage && !acceptedPermissions.canPos" class="info-message">
                <p class="text-body-1 text-typo mb-0">
                  Du kannst dich jetzt in der App anmelden und die Ticket-Scanner Funktion nutzen.
                </p>
              </div>
            </v-card-text>

            <v-card-actions class="pa-8 pt-0 d-flex justify-center">
              <v-btn
                v-if="acceptedPermissions.canCheck && !acceptedPermissions.canManage && !acceptedPermissions.canPos"
                x-large
                @click="$router.push('/')"
                class="success-btn"
                elevation="0"
              >
                <i class="material-icons-round me-2">home</i>
                Zur Website
              </v-btn>
              <v-btn
                v-else
                x-large
                @click="isUserLoggedIn ? $router.push('/admin/analytics') : goToLogin()"
                class="success-btn"
                elevation="0"
              >
                <i class="material-icons-round me-2">{{ isUserLoggedIn ? 'dashboard' : 'login' }}</i>
                {{ isUserLoggedIn ? 'Zum Admin-Bereich' : 'Jetzt anmelden' }}
              </v-btn>
            </v-card-actions>
          </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from 'axios'

// Use the same base URL as axiosAuth
const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_BASEURL || 'http://localhost:3001'
})

export default {
  name: 'AcceptInvitation',
  
  // CRITICAL: This runs BEFORE mounted
  beforeCreate() {
    console.log('🔥 [BEFORE CREATE] AcceptInvitation component beforeCreate hook');
  },
  
  created() {
    console.log('🔥 [CREATED] AcceptInvitation component created hook');
  },
  
  data() {
    return {
      loading: true,
      accepting: false,
      accepted: false,
      error: null,
      invitation: null,
      managerExists: false,
      existingManager: null,
      acceptedPermissions: {
        canManage: false,
        canPos: false,
        canCheck: false
      },
      token: null
    }
  },
  computed: {
    isExpiringSoon() {
      if (!this.invitation || !this.invitation.expiresAt) return false
      const expiryDate = new Date(this.invitation.expiresAt)
      const now = new Date()
      const hoursUntilExpiry = (expiryDate - now) / (1000 * 60 * 60)
      return hoursUntilExpiry < 48 && hoursUntilExpiry > 0
    },
    
    isUserLoggedIn() {
      return !!localStorage.getItem('token')
    }
  },
  methods: {
    getRoleDisplayName(role) {
      const roleNames = {
        'manager': 'Manager',
        'pos': 'POS-Nutzer',
        'checker': 'Kontrolleur'
      }
      return roleNames[role] || role
    },

    getRoleColor(role) {
      const roleColors = {
        'manager': '#cb0c9f',
        'pos': '#4caf50',
        'checker': '#00bcd4'
      }
      return roleColors[role] || '#9e9e9e'
    },

    getExistingManagerName() {
      if (!this.existingManager) return this.invitation.email
      
      // Backend sends 'name' field (firstname + lastname)
      const name = this.existingManager.name || ''
      const trimmedName = name.trim()
      
      // Return name if not empty, otherwise companyName, otherwise email
      return trimmedName || this.existingManager.companyName || this.existingManager.email || this.invitation.email
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async loadInvitation() {
      this.loading = true
      this.error = null

      try {
        console.log('🔍 [LOAD] Loading invitation with token:', this.token)
        console.log('🔍 [LOAD] API URL:', `/invitations/${this.token}`)
        
        const response = await axiosInstance.get(
          `/invitations/${this.token}`
        )

        this.invitation = response.data.invitation
        this.managerExists = response.data.managerExists
        this.existingManager = response.data.existingManager

        console.log('✅ [LOAD] Invitation loaded:', this.invitation)
        console.log('👤 [LOAD] Manager exists:', this.managerExists)

      } catch (error) {
        console.error('❌ Error loading invitation:', error)
        this.error = (error.response && error.response.data && error.response.data.message) || 
                     (error.response && error.response.data) || 
                     'Einladung nicht gefunden oder abgelaufen'
      } finally {
        this.loading = false
      }
    },

    async acceptInvitation() {
      console.log('🔔 [ACCEPT] Button clicked! Token:', this.token)
      
      this.accepting = true
      this.error = null

      try {
        console.log('✅ [ACCEPT] Sending POST request to:', `/invitations/${this.token}/accept`)
        
        const response = await axiosInstance.post(
          `/invitations/${this.token}/accept`
        )

        // Extract permissions from response
        if (response.data.invitation) {
          this.acceptedPermissions = {
            canManage: response.data.invitation.canManage || false,
            canPos: response.data.invitation.canPos || false,
            canCheck: response.data.invitation.canCheck || false
          }
        }
        this.accepted = true

        console.log('✅ Invitation accepted:', response.data)
        console.log('✅ Accepted permissions:', this.acceptedPermissions)

        // For "checker only" role, don't redirect - just show success message
        if (this.acceptedPermissions.canCheck && !this.acceptedPermissions.canManage && !this.acceptedPermissions.canPos) {
          console.log('✅ Checker-only role assigned - no automatic redirect')
          return
        }

        // Check if user is already logged in
        const isLoggedIn = !!localStorage.getItem('token')
        console.log('🔐 User is logged in:', isLoggedIn)

        if (isLoggedIn) {
          // User is logged in - refresh user info to load new roles
          console.log('🔄 Refreshing user info to load new roles...')
          
          setTimeout(async () => {
            try {
              // Dispatch userInformations and WAIT for it to complete
              await this.$store.dispatch('userInformations')
              console.log('✅ User info refreshed successfully!')
              console.log('🔐 Current role:', localStorage.getItem('currentRole'))
              console.log('🔐 Current manager:', localStorage.getItem('currentForManagerId'))
              
              // Now redirect
              console.log('🚀 Redirecting to admin area...')
              this.$router.push('/admin/analytics')
            } catch (error) {
              console.error('❌ Failed to refresh user info:', error)
              // Fallback: reload page
              window.location.href = '/admin/analytics'
            }
          }, 1500)
        } else {
          // User not logged in - redirect to login
          setTimeout(() => {
            console.log('🚀 Redirecting to login...')
            this.goToLogin()
          }, 2000)
        }

      } catch (error) {
        console.error('❌ Error accepting invitation:', error)
        this.error = (error.response && error.response.data && error.response.data.message) || 
                     (error.response && error.response.data) || 
                     'Fehler beim Annehmen der Einladung'
        this.accepted = false
      } finally {
        this.accepting = false
      }
    },

    goToLogin() {
      this.$router.push('/admin/login')
    }
  },

  mounted() {
    console.log('🎬 [MOUNTED] AcceptInvitation component mounted')
    console.log('🎬 [MOUNTED] Route params:', this.$route.params)
    
    this.token = this.$route.params.token
    
    console.log('🎬 [MOUNTED] Token:', this.token)
    
    if (!this.token) {
      console.error('❌ [MOUNTED] No token found in route params')
      this.error = 'Kein Einladungstoken gefunden'
      this.loading = false
      return
    }

    this.loadInvitation()
  }
}
</script>

<style scoped>
/* Page Background */
.invitation-page {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
  min-height: 100vh;
}

/* Main Card */
.invitation-card {
  border-radius: 24px !important;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.3s ease;
}

/* Header Styles */
.invitation-header {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
  padding: 48px 32px;
  text-align: center;
  position: relative;
}

.invitation-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="rgba(255,255,255,0.1)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>') no-repeat bottom;
  background-size: cover;
  opacity: 0.3;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
}

.icon-wrapper i {
  color: #ffffff;
  font-size: 40px;
}

.header-title {
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
  position: relative;
  z-index: 1;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin: 0;
  position: relative;
  z-index: 1;
}

/* Info Box */
.info-box {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 24px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.info-row i {
  color: #fb6340;
  font-size: 24px;
  margin-top: 4px;
}

.info-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  color: #344767;
  font-weight: 600;
}

/* Permissions Box */
.permissions-box {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 16px;
  padding: 24px;
  border: 2px solid rgba(102, 126, 234, 0.1);
}

.permissions-title {
  color: #344767;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.permissions-title i {
  color: #fb6340;
  font-size: 20px;
}

.permissions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.permissions-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  color: #344767;
  font-size: 14px;
  line-height: 1.6;
}

.permissions-list li:last-child {
  margin-bottom: 0;
}

.permissions-list li i {
  color: #4caf50;
  font-size: 18px;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Status Boxes */
.status-box {
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.status-box i {
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.status-box strong {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
}

.status-box p {
  margin: 0;
  font-size: 13px;
  color: #6c757d;
}

.status-box.success {
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.status-box.success i {
  color: #4caf50;
}

.status-box.success strong {
  color: #2e7d32;
}

.status-box.info {
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
}

.status-box.info i {
  color: #2196f3;
}

.status-box.info strong {
  color: #1565c0;
}

.status-box.warning {
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.status-box.warning i {
  color: #ff9800;
}

.status-box.warning strong {
  color: #e65100;
}

.status-box.default {
  background: rgba(108, 117, 125, 0.1);
  border: 1px solid rgba(108, 117, 125, 0.3);
}

.status-box.default i {
  color: #6c757d;
}

.status-box.default strong {
  color: #495057;
}

/* Accept Button */
.accept-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: #ffffff !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  border-radius: 12px !important;
  padding: 0 48px !important;
  height: 56px !important;
  transition: all 0.3s ease !important;
}

.accept-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
}

/* Error Card */
.error-card .error-header {
  background: linear-gradient(135deg, #f44336 0%, #e91e63 100%);
  padding: 48px 32px;
  text-align: center;
  position: relative;
}

.error-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon-wrapper i {
  color: #ffffff;
  font-size: 40px;
}

.error-title {
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
}

.error-reasons {
  background: #fff3f3;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #ffcdd2;
}

.error-reasons h6 {
  color: #c62828;
}

.error-reasons ul {
  margin: 0;
  padding-left: 20px;
  color: #d32f2f;
}

.error-reasons li {
  margin-bottom: 8px;
}

/* Success Card */
.success-card .success-header {
  background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%);
  padding: 48px 32px;
  text-align: center;
  position: relative;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: success-pulse 2s infinite;
}

@keyframes success-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.success-icon-wrapper i {
  color: #ffffff;
  font-size: 40px;
}

.success-title {
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
}

.success-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin: 0;
}

.redirect-info {
  margin-top: 32px;
}

.success-btn {
  background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%) !important;
  color: #ffffff !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  border-radius: 12px !important;
  padding: 0 48px !important;
  height: 56px !important;
  transition: all 0.3s ease !important;
}

.success-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4) !important;
}
</style>
